import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '../innovher/PageHeader';
import ApplyCta from '../innovher/ApplyCta';
import { EditorialIntro, IntroSplit } from '../innovher/SplitSection';
import { IMG } from '../../lib/images';
import SEO from '../SEO';

const ONGOING_BUILDS = [
  { name: 'EnCODE', label: 'enCODE', to: '/ventures/encode', logo: '/new_images/logos/encode.webp' },
  {
    name: 'Ctpl.Online',
    label: 'CTPL',
    to: '/ventures/ctpl',
    logo: '/new_images/logos/ctpl.online.webp',
  },
  {
    name: 'BaX',
    label: 'Beyond Ability X',
    to: '/ventures/beyond-ability-x',
    logo: '/new_images/logos/Beyond Ability X Logo_Purple.webp',
  },
  { name: 'i2i connect', label: 'i2i', to: '/ventures/i2i', logo: '/new_images/logos/I2I.webp' },
];

const WORK_AREAS = [
  'Venture strategy',
  'Product development',
  'Business models',
  'Market validation',
  'Partnerships',
  'Technology',
  'Go-to-market',
];

export default function VentureStudioPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="InnovVentures | Venture Studio"
        description="InnovVentures is InnovHer's venture studio arm, where opportunities are identified, concepts are shaped and new businesses are built, including enCODE, BAX, Bharat Ventures, CTPL and i2i."
        keywords="InnovVentures, Venture Studio InnovHer, venture studio India, co-build startups, enCODE, BAX, Bharat Ventures, CTPL, i2i"
      />
      {/* Header / Purpose Section */}
      <section className="relative bg-[#08080a] pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 lg:pb-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <EditorialIntro
            className="reveal-up"
            eyebrow="InnovVentures"
            logo="/new_images/logos/venturestudio.webp"
            title={
              <>
                From possibility to <span className="text-[#d42b92]">enterprise</span>.
              </>
            }
            lead="The venture studio arm, where opportunities are identified, concepts are shaped and new businesses are built, hands-on from within."
          >
            <p>
              InnovVentures is the venture studio arm, where opportunities are identified, concepts
              are shaped and new businesses are built.
            </p>
            <p>
              The studio works hands-on across venture strategy, product development, business
              models, market validation, partnerships, technology and go-to-market, bringing
              together the capabilities required to move an idea towards an investable and scalable
              enterprise.
            </p>
          </EditorialIntro>
        </div>
      </section>

      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5 overflow-hidden">
        <div className="absolute top-1/2 right-0 w-[420px] h-[420px] rounded-full bg-[#d42b92]/10 blur-[140px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: Text content */}
            <div className="reveal-up">
              <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">How we build</div>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Hands-on across the{' '}
                <span className="text-[#d42b92]">full venture journey</span>
              </h2>
              <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed">
                Some ventures originate within the InnovHer ecosystem; others are built alongside
                founders, institutions and strategic partners.
              </p>
              <p className="mt-4 text-white/60 text-base sm:text-lg leading-relaxed">
                The approach goes beyond advising businesses from the outside. InnovVentures
                participates in the building journey, creating, validating and strengthening ventures
                from within.
              </p>

              <div className="mt-8 flex flex-wrap gap-2.5">
                {WORK_AREAS.map((label) => (
                  <span
                    key={label}
                    className="px-4 py-2.5 rounded-full text-sm border border-white/10 bg-white/[0.03] text-white/75"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Right: Image */}
            <div className="reveal-up relative">
              <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">
                <img
                  src="/ventstu.jpeg"
                  alt="InnovVentures venture studio journey"
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#d42b92]/20 to-transparent pointer-events-none" />
              </div>
              <div className="absolute -inset-4 -z-10 rounded-3xl bg-[#d42b92]/8 blur-2xl pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up max-w-2xl mb-10">
            <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">Ongoing builds</div>
            {/* <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl leading-[1.08]">
              Ventures in the studio now
            </h2> */}
          </div>

          <div className="reveal-up grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ONGOING_BUILDS.map((v) => (
              <Link
                key={v.to}
                to={v.to}
                className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-white/25 hover:bg-white/[0.05] transition-all duration-300"
              >
                <div className="h-20 w-full rounded-xl bg-white/95 flex items-center justify-center p-3 group-hover:bg-white transition-colors">
                  <img
                    src={v.logo}
                    alt={v.name}
                    className="max-h-9 w-auto max-w-[85%] object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <div className="mt-3.5 flex items-center justify-between px-1">
                  <span className="font-semibold text-base text-white group-hover:text-[#ff8ec5] transition-colors">
                    {v.name}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#ff8ec5] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-[#08080a] pb-24 lg:pb-32 pt-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up rounded-[2rem] border border-[#d42b92]/30 bg-gradient-to-br from-[#d42b92]/20 via-black to-black p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-[420px] h-[420px] bg-[#d42b92]/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight">
                From possibility to enterprise
              </h2>
              <p className="mt-4 text-white/65 text-lg leading-relaxed">
                Ready to build with InnovVentures, inside the ecosystem or alongside founders and
                partners?
              </p>
              <div className="mt-8">
                <ApplyCta to="/contact" label="Contact InnovHer" size="lg" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
